import { CheckIcon, ChevronRightIcon } from 'lucide-react'
import { DropdownMenu as Menu } from 'radix-ui'
import type * as React from 'react'
import { cn } from '@/lib/utils'

export const DropdownMenu = Menu.Root
export const DropdownMenuTrigger = Menu.Trigger
export const DropdownMenuGroup = Menu.Group
export const DropdownMenuSub = Menu.Sub
export const DropdownMenuRadioGroup = Menu.RadioGroup

export const menuSurface = cn(
  'z-50 min-w-44 overflow-hidden rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-elevation-3',
  // Misma entrada para menús, selects, popovers y combobox: fundido + escala 0.97 → 1 con la curva de la app.
  'origin-(--radix-popper-transform-origin) duration-150 ease-smooth data-[state=closed]:duration-100',
  'data-[state=closed]:animate-out data-[state=open]:animate-in data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'data-[state=closed]:zoom-out-[0.97] data-[state=open]:zoom-in-[0.97]',
  'data-[side=bottom]:slide-in-from-top-1 data-[side=top]:slide-in-from-bottom-1 data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1',
)

export const menuItem = cn(
  'relative flex cursor-default select-none items-center gap-2.5 rounded-md px-2 py-1.5 text-sm outline-none',
  'data-[disabled]:pointer-events-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:opacity-50',
  '[&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-muted-foreground',
)

export function DropdownMenuContent({
  className,
  sideOffset = 6,
  ...props
}: React.ComponentProps<typeof Menu.Content>) {
  return (
    <Menu.Portal>
      <Menu.Content sideOffset={sideOffset} className={cn(menuSurface, className)} {...props} />
    </Menu.Portal>
  )
}

interface ItemProps extends React.ComponentProps<typeof Menu.Item> {
  destructive?: boolean
  shortcut?: string
}

export function DropdownMenuItem({ className, destructive, shortcut, children, ...props }: ItemProps) {
  return (
    <Menu.Item
      className={cn(
        menuItem,
        destructive &&
          'text-destructive data-[highlighted]:bg-destructive-soft data-[highlighted]:text-destructive [&_svg]:text-destructive',
        className,
      )}
      {...props}
    >
      {shortcut ? (
        <>
          {children}
          <span className="ml-auto pl-4 text-muted-foreground text-xs tracking-wide">{shortcut}</span>
        </>
      ) : (
        children
      )}
    </Menu.Item>
  )
}

export function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof Menu.RadioItem>) {
  return (
    <Menu.RadioItem className={cn(menuItem, 'pr-8', className)} {...props}>
      {children}
      <Menu.ItemIndicator className="absolute right-2 flex">
        <CheckIcon className="text-primary!" />
      </Menu.ItemIndicator>
    </Menu.RadioItem>
  )
}

export function DropdownMenuLabel({ className, ...props }: React.ComponentProps<typeof Menu.Label>) {
  return (
    <Menu.Label
      className={cn('px-2 py-1.5 font-medium text-muted-foreground text-xs', className)}
      {...props}
    />
  )
}

export function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<typeof Menu.Separator>) {
  return <Menu.Separator className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />
}

export function DropdownMenuSubTrigger({
  className,
  children,
  ...props
}: React.ComponentProps<typeof Menu.SubTrigger>) {
  return (
    <Menu.SubTrigger className={cn(menuItem, 'data-[state=open]:bg-accent', className)} {...props}>
      {children}
      <ChevronRightIcon className="ml-auto" />
    </Menu.SubTrigger>
  )
}

export function DropdownMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof Menu.SubContent>) {
  return (
    <Menu.Portal>
      <Menu.SubContent className={cn(menuSurface, className)} {...props} />
    </Menu.Portal>
  )
}
